// The register: the shared view of what is in flight, replacing the spreadsheet and the email
// thread.
//
// Its entire state - filter, sort, page - is the URL. `setSearchParams` is the only way it
// changes, and the effect that fetches depends on the URL, so the screen cannot disagree with the
// address bar and a filtered view is a link someone can be sent.
//
// With the deal flow on (TX-US-0043), the stage takes the state's place - its column and its
// filter - and Waiting for me lists the deals whose open approval step is the reader's.

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { listDeals } from '../api.js';
import { useMe } from '../MeContext.js';
import { DEAL_EDITOR_ROLES, holdsAny } from '../roles.js';
import Icon from '../ui/Icon.jsx';
import Pager from '../ui/Pager.jsx';
import useMediaQuery from '../ui/useMediaQuery.js';
import SearchFilter from '../ui/SearchFilter.jsx';
import usePageTrail from '../ui/usePageTrail.js';
import DealTable from './DealTable.jsx';
import { STATES } from './dealForm.js';
import DealTiles from './DealTiles.jsx';
import { STAGE_LABELS, flowOn } from './flow.js';
import {
  columnsFor,
  fromSearchParams,
  nextSort,
  toApiSearch,
  toQueryString,
  withCursor,
  withFilter,
} from './registerUrl.js';

const PAGE_SIZE = 25;

// Where the table stops fitting, and therefore where tiles take over.
//
// Derived, not chosen: the columns have a natural minimum around 855px and the shell is 90% of
// the window, so the table needs roughly 950px of viewport. 60rem is the round number below that.
//
// Declared here and nowhere else. A matching breakpoint in the stylesheet would be a second copy
// that has to agree with this one, and the presentation is chosen in JS precisely so that only one
// of the two is ever in the DOM - so the stylesheet has no decision to make.
const TILES_BELOW = '(max-width: 60rem)';

export default function DealRegister() {
  // The API refuses a create without one of the deal-editing roles regardless (ADR-0012); this
  // only avoids offering an action that would be refused. Hiding a control is not authorisation,
  // and must never be the only thing standing between a caller and an endpoint.
  const me = useMe();
  const canCapture = holdsAny(me, DEAL_EDITOR_ROLES);
  const flow = flowOn(me);

  const [searchParams, setSearchParams] = useSearchParams();
  const query = fromSearchParams(searchParams);
  const tiles = useMediaQuery(TILES_BELOW);

  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);

  // `replace` exists for the counterparty filter, which writes as you type: pushing every
  // debounced value would fill the history with one entry per pause and make the back button walk
  // backwards through a half-typed name.
  const apply = useCallback(
    (next, { replace = false } = {}) =>
      setSearchParams(new URLSearchParams(toQueryString(next)), { replace }),
    [setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);

    listDeals(toApiSearch(fromSearchParams(searchParams), PAGE_SIZE))
      .then((result) => {
        if (!cancelled) setPage(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });

    return () => {
      cancelled = true;
    };
    // The URL is the dependency. Anything else here would be a second source of truth.
  }, [searchParams]);

  const paging = usePageTrail({ query, page, apply, withCursor });

  const onSort = (key) => {
    paging.reset();
    apply(nextSort(query, key));
  };

  const onFilter = (name, value, options) => {
    paging.reset();
    apply(withFilter(query, name, value), options);
  };

  return (
    <section className="register">
      <header className="register-header">
        <h2>Deals</h2>
        {/* Icon-only, with the accessible name it used to carry as text: the label is what a
            screen reader announces and what the e2e suite locates it by. */}
        {canCapture && (
          <Link className="icon-button primary" to="/deals/new"
                aria-label="Add deal" title="Add deal">
            <Icon name="plus" />
          </Link>
        )}
      </header>

      <div className="filters">
        <SearchFilter
          label="Counterparty"
          placeholder="exact name"
          value={query.counterparty}
          onCommit={(value, options) => onFilter('counterparty', value, options)}
        />
        {flow ? (
          <>
            <label className="field">
              <span className="field-label">Stage</span>
              <select value={query.stage ?? ''} onChange={(e) => onFilter('stage', e.target.value)}>
                <option value="">Any</option>
                {Object.entries(STAGE_LABELS).map(([stage, label]) => (
                  <option key={stage} value={stage}>{label}</option>
                ))}
              </select>
            </label>
            <label className="field waiting-toggle">
              <input type="checkbox" checked={query.waiting === 'true'}
                     onChange={(e) => onFilter('waiting', e.target.checked ? 'true' : null)} />
              Waiting for me
            </label>
          </>
        ) : (
          <label className="field">
            <span className="field-label">State</span>
            <select value={query.state ?? ''} onChange={(e) => onFilter('state', e.target.value)}>
              <option value="">Any</option>
              {STATES.map((state) => (
                <option key={state.value} value={state.value}>{state.label}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      {/* One presentation is in the DOM at a time. Rendering both and hiding one would put every
          deal into the accessibility tree twice, and give every test two matches for one row. */}
      {page && (tiles
        ? <DealTiles flow={flow} deals={page.deals} query={query} onSort={onSort} />
        : <DealTable columns={columnsFor(flow)} deals={page.deals} query={query}
                     onSort={onSort} />)}

      {busy && <p className="muted">Loading…</p>}

      {!busy && page?.deals.length === 0 && (
        <p className="empty">
          No deals here.
          {canCapture && <> <Link to="/deals/new">Add the first one</Link>.</>}
        </p>
      )}

      <Pager
        busy={busy}
        hasPrevious={paging.hasPrevious}
        hasMore={Boolean(page?.has_more)}
        onPrevious={paging.onPrevious}
        onNext={paging.onNext}
      />
    </section>
  );
}
